"use client";
/* eslint-disable react-hooks/set-state-in-effect */

// Halaman dukungan (tip jar) — untuk FANS, nada hangat & personal.
// Mobile-first: mayoritas pengunjung datang dari link bio di HP.

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Heart, CreditCard, User } from "lucide-react";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant, useServiceFeeBps } from "@/components/tenant/TenantProvider";
import { checkoutApi } from "@/lib/api-client";
import { formatRupiah } from "@/lib/utils";
import { PAYMENT_METHODS, type PaymentMethodId } from "@/lib/payment-methods";
import { useTurnstile, isTurnstileError } from "@/hooks/use-turnstile";
import { inputClass } from "@/lib/form-classes";

interface TipConfig {
  enabled: boolean;
  title?: string;
  message?: string;
  preset_amounts?: number[];
  target_amount?: number | null;
  thank_you?: string | null;
  raised?: number;
}

interface UserInfo {
  name: string;
  email: string;
  phone: string | null;
}

const MIN_AMOUNT = 5000;

const formatThousands = (digits: string) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

export default function DukunganPageClient() {
  const { brand_name } = useTenant();
  const feeRateBps = useServiceFeeBps();
  const router = useRouter();

  const [config, setConfig] = useState<TipConfig | null>(null);
  const [configState, setConfigState] = useState<"loading" | "active" | "disabled" | "error">("loading");

  // Auth
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [mobile, setMobile] = useState("");
  const [guestForm, setGuestForm] = useState({ name: "", email: "", phone: "" });
  const turnstile = useTurnstile("turnstile-spin-v2");
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Nominal
  const [amountRaw, setAmountRaw] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodId>("qris");

  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2)
  );

  useEffect(() => {
    fetch("/api/public/tip", { credentials: "include" })
      .then(async (r) => {
        if (r.status === 404) {
          setConfigState("disabled");
          return;
        }
        const d = await r.json().catch(() => null);
        if (r.ok && d?.success && d.data) {
          if (d.data.enabled === false) {
            setConfigState("disabled");
          } else {
            setConfig(d.data as TipConfig);
            setConfigState("active");
          }
        } else {
          setConfigState(r.ok ? "disabled" : "error");
        }
      })
      .catch(() => setConfigState("error"));
  }, []);

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) {
          setIsLoggedIn(false);
        } else {
          const u = d.data?.user || d.data;
          setIsLoggedIn(true);
          setUser({ name: u.name || "", email: u.email || "", phone: u.phone || null });
        }
      })
      .catch(() => setIsLoggedIn(false));
  }, []);

  const amount = parseInt(amountRaw || "0", 10) || 0;
  const amountInvalid = amount < MIN_AMOUNT;
  const serviceFee = Math.round((amount * feeRateBps) / 10000);
  const displayTotal = amount + serviceFee;

  const presets = (config?.preset_amounts ?? []).filter((n) => typeof n === "number" && n >= MIN_AMOUNT).slice(0, 6);
  const chips = presets.length > 0 ? presets : [10000, 25000, 50000];

  const target = typeof config?.target_amount === "number" && config.target_amount > 0 ? config.target_amount : null;
  const raised = typeof config?.raised === "number" && config.raised > 0 ? config.raised : 0;
  const progressPct = target ? Math.min(100, Math.round((raised / target) * 100)) : 0;

  const createTipPayment = async (mobileValue?: string) => {
    const res = await checkoutApi.createPayment(
      {
        order_type: "tip",
        product_id: "tip",
        payment_method: paymentMethod,
        custom_amount: amount,
        ...(mobileValue ? { mobile: mobileValue } : {}),
      },
      idempotencyKey
    );
    const d = res.data as { order_id?: string; free?: boolean; order_page_url?: string } | undefined;
    if (res.success && d?.free && d.order_page_url) {
      window.location.href = d.order_page_url;
      return;
    }
    if (res.success && d?.order_id) {
      router.push(`/checkout/${d.order_id}`);
      return;
    }
    setError(res.error || "Gagal memproses dukungan. Silakan coba lagi.");
    setSending(false);
  };

  const handleSupport = async () => {
    setError("");
    if (amountInvalid) {
      setAmountTouched(true);
      return;
    }
    setSending(true);

    if (isLoggedIn) {
      await createTipPayment(user?.phone ? undefined : mobile.trim() || undefined);
      return;
    }

    // Guest: buat akun ringan dulu (tanpa password, skip-OTP — efek samping
    // donasi, bukan signup langsung) — pola sama dengan halaman order
    const { name, email, phone } = guestForm;
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError("Nama, email, dan WhatsApp wajib diisi ya.");
      setSending(false);
      return;
    }
    setIsDuplicate(false);
    const regResponse = await fetch("/api/auth/register-guest", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        cfTurnstileResponse: turnstile.token,
      }),
    });
    const regRes = await regResponse.json().catch(() => ({ success: false }));
    if (!regRes.success) {
      // Token Turnstile SEKALI PAKAI dan sudah dikonsumsi server sebelum cek
      // duplikat — tanpa reset, submit ulang setelah 409 pasti ditolak.
      if (isTurnstileError(regRes.errorType)) {
        turnstile.noteServerRejection(regRes.errorType);
        setError(regRes.error);
      } else {
        turnstile.reset();
        if (regResponse.status === 409) {
          setIsDuplicate(true);
        } else {
          setError(regRes.error || "Gagal memproses data kamu. Coba lagi.");
        }
      }
      setSending(false);
      return;
    }
    await createTipPayment(phone.trim());
  };

  // ── Loading ──
  if (configState === "loading" || (configState === "active" && isLoggedIn === null)) {
    return (
      <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-3">
          <div className="h-40 rounded-2xl bg-black/[0.05] animate-pulse" />
          <div className="h-64 rounded-2xl bg-black/[0.05] animate-pulse" />
        </div>
      </main>
    );
  }

  // ── Nonaktif / error ──
  if (configState !== "active" || !config) {
    return (
      <main id="main-content" tabIndex={-1} className="min-h-dvh bg-warm-cream flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[rgba(12,12,10,0.08)] shadow-sm p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-warm-cream">
            <Heart className="h-7 w-7" style={{ color: "rgba(12,12,10,0.65)" }} />
          </div>
          <p className="font-semibold text-warm-black mb-1.5">
            {configState === "error" ? "Gagal memuat halaman" : "Halaman dukungan belum aktif"}
          </p>
          <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(12,12,10,0.65)" }}>
            {configState === "error"
              ? "Terjadi kendala saat memuat. Muat ulang halaman atau coba beberapa saat lagi."
              : `${brand_name} belum membuka halaman dukungan. Sampaikan langsung ke kreatornya kalau kamu ingin mendukung, ya.`}
          </p>
          <Link href="/" className="text-sm font-semibold text-primary hover:underline">
            ← Kembali ke Beranda
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-dvh bg-warm-cream flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(12,12,10,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(12,12,10,0.04) 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      }}
    >
      <div className="w-full max-w-md space-y-3 py-6">
        <div className="bg-white rounded-2xl border border-[rgba(12,12,10,0.08)] shadow-sm overflow-hidden">
          <div className="h-1 bg-primary w-full" />

          {/* Kreator: logo + judul + pesan personal */}
          <div className="px-6 pt-6 pb-5 text-center">
            <div className="flex justify-center mb-4">
              <BrandLogo width={128} height={32} priority className="h-8 w-auto" />
            </div>
            <h1 className="text-xl font-bold text-warm-black leading-snug">
              {config.title?.trim() || `Dukung ${brand_name}`}
            </h1>
            {config.message?.trim() && (
              <p className="mt-2 text-sm leading-relaxed whitespace-pre-line" style={{ color: "rgba(12,12,10,0.65)" }}>
                {config.message.trim()}
              </p>
            )}
          </div>

          {/* Progress menuju target */}
          {target && (
            <div className="px-6 pb-5">
              <div className="rounded-xl bg-warm-cream border border-[rgba(12,12,10,0.08)] px-4 py-3.5">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <p className="text-sm font-semibold text-warm-black tabular-nums">
                    {formatRupiah(raised)}{" "}
                    <span className="font-normal text-xs" style={{ color: "rgba(12,12,10,0.65)" }}>
                      terkumpul dari {formatRupiah(target)}
                    </span>
                  </p>
                  <span className="shrink-0 text-xs font-bold text-primary tabular-nums">{progressPct}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-black/[0.07]">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPct}%` }}
                    role="progressbar"
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Nominal dukungan ── */}
          <div className="h-px bg-black/[0.06]" />
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Heart className="h-4 w-4" style={{ color: "rgba(12,12,10,0.65)" }} />
              <span className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "rgba(12,12,10,0.65)" }}>
                Nominal Dukungan
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              {chips.map((amt) => {
                const active = amount === amt;
                return (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => {
                      setAmountRaw(String(amt));
                      setAmountTouched(true);
                    }}
                    className={`min-h-[48px] rounded-xl border px-2 py-2.5 text-sm font-semibold tabular-nums transition-colors ${
                      active
                        ? "border-primary bg-[var(--brand-primary-soft)] text-primary"
                        : "border-[rgba(12,12,10,0.12)] bg-white text-warm-black hover:border-[rgba(12,12,10,0.25)]"
                    }`}
                  >
                    {formatRupiah(amt)}
                  </button>
                );
              })}
            </div>
            <div
              className={`flex items-center gap-2 rounded-xl border bg-white px-3.5 py-3 transition-colors ${
                amountTouched && amountInvalid ? "border-red-300" : "border-[rgba(12,12,10,0.12)] focus-within:border-primary"
              }`}
            >
              <span className="text-base font-semibold" style={{ color: "rgba(12,12,10,0.65)" }}>
                Rp
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={amountRaw ? formatThousands(amountRaw) : ""}
                onChange={(e) => {
                  setAmountRaw(e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 10));
                  setAmountTouched(true);
                }}
                placeholder={formatThousands(String(MIN_AMOUNT))}
                aria-label="Nominal dukungan bebas"
                className="w-full text-lg font-bold text-warm-black tabular-nums outline-none bg-transparent"
              />
            </div>
            {amountTouched && amountInvalid ? (
              <p className="text-xs text-red-500 mt-1.5">Minimal {formatRupiah(MIN_AMOUNT)}</p>
            ) : (
              <p className="text-xs mt-1.5" style={{ color: "rgba(12,12,10,0.65)" }}>
                Nominal bebas, minimal {formatRupiah(MIN_AMOUNT)}.
              </p>
            )}
          </div>

          {/* ── Data pendukung ── */}
          <div className="h-px bg-black/[0.06]" />
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4" style={{ color: "rgba(12,12,10,0.65)" }} />
              <span className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "rgba(12,12,10,0.65)" }}>
                Data Kamu
              </span>
            </div>

            {isLoggedIn && user ? (
              <div className="space-y-2 text-sm">
                <p className="text-warm-black font-medium">{user.name}</p>
                <p style={{ color: "rgba(12,12,10,0.65)" }}>{user.email}</p>
                {!user.phone && (
                  <div className="pt-1">
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: "rgba(12,12,10,0.65)" }}>
                      WhatsApp <span style={{ color: "rgba(12,12,10,0.65)" }}>(untuk konfirmasi pembayaran)</span>
                    </label>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="08xxxxxxxxxx"
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {(
                  [
                    { key: "name", label: "Nama", type: "text", placeholder: "Nama kamu" },
                    { key: "email", label: "Email", type: "email", placeholder: "nama@email.com" },
                    { key: "phone", label: "WhatsApp", type: "tel", placeholder: "08xxxxxxxxxx" },
                  ] as const
                ).map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: "rgba(12,12,10,0.65)" }}>
                      {f.label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type={f.type}
                      value={guestForm[f.key]}
                      onChange={(e) => {
                        setGuestForm((prev) => ({ ...prev, [f.key]: e.target.value }));
                        if (isDuplicate) setIsDuplicate(false);
                      }}
                      placeholder={f.placeholder}
                      className={inputClass}
                    />
                  </div>
                ))}
                {turnstile.widget}
              </div>
            )}

            {isDuplicate && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5">
                <p className="text-xs font-semibold text-amber-800 mb-0.5">Email atau WhatsApp sudah terdaftar</p>
                <p className="text-[11px] text-amber-700 mb-2">Masuk dulu, lalu kembali ke halaman ini ya.</p>
                <Link
                  href="/login?redirect=/dukungan"
                  className="inline-block rounded-md bg-[var(--brand-primary)] px-3 py-1.5 text-[11px] font-semibold text-[var(--brand-on-primary,#fff)] hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  Masuk
                </Link>
              </div>
            )}
          </div>

          {/* ── Metode pembayaran ── */}
          <div className="h-px bg-black/[0.06]" />
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-4 w-4" style={{ color: "rgba(12,12,10,0.65)" }} />
              <span className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "rgba(12,12,10,0.65)" }}>
                Metode Pembayaran
              </span>
            </div>
            <div className="space-y-2">
              {PAYMENT_METHODS.map((m) => (
                <label
                  key={m.id}
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    paymentMethod === m.id
                      ? "border-primary bg-[var(--brand-primary-soft)]"
                      : "border-[rgba(12,12,10,0.12)] bg-white hover:border-[rgba(12,12,10,0.25)]"
                  }`}
                >
                  <input
                    type="radio"
                    name="tip-payment-method"
                    checked={paymentMethod === m.id}
                    onChange={() => setPaymentMethod(m.id)}
                    className="h-4 w-4 text-primary focus:ring-primary"
                  />
                  <span className="flex-1 text-sm font-medium text-warm-black">{m.label}</span>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="mx-5 mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* ── Footer: total + CTA ── */}
        <div className="bg-white rounded-2xl border border-[rgba(12,12,10,0.08)] shadow-sm px-5 py-3.5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xl font-bold text-warm-black tabular-nums">
                {amount > 0 ? formatRupiah(displayTotal) : "Rp0"}
              </p>
              {serviceFee > 0 && amount > 0 && (
                <p className="text-xs tabular-nums" style={{ color: "rgba(12,12,10,0.65)" }}>
                  termasuk biaya layanan {formatRupiah(serviceFee)}
                </p>
              )}
            </div>
            <button
              onClick={handleSupport}
              disabled={sending || amountInvalid}
              className="min-h-[48px] py-2.5 px-6 bg-primary text-[var(--brand-on-primary,#fff)] text-sm font-semibold rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
            >
              {sending ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Memproses...
                </>
              ) : (
                <>
                  <Heart className="h-4 w-4" />
                  Kirim Dukungan
                </>
              )}
            </button>
          </div>
          {amountTouched && amountInvalid && (
            <p className="mt-2 text-xs text-red-500">Isi nominal minimal {formatRupiah(MIN_AMOUNT)} dulu ya.</p>
          )}
        </div>

        <p className="text-center text-xs" style={{ color: "rgba(12,12,10,0.65)" }}>
          Dukunganmu diproses aman lewat halaman pembayaran {brand_name}.
        </p>
      </div>
    </main>
  );
}
